/**
 * 变换工具插件 —— 对选中对象进行平移、旋转、缩放变换。
 * 基于 THREE.TransformControls 实现 gizmo 交互。
 */
import type * as THREE from 'three'
import { TransformControls } from 'three/addons/controls/TransformControls.js'
import { WEBGL2_ONLY_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { getOptionalViewRegistry, isActiveViewFrame } from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type {
  AssetKind,
  InputConsumer,
  NormalizedKeyEvent,
  NormalizedPointerEvent,
} from '@/shared/types/core'
import type { ActivatableToolPlugin, ActivationToken } from '@/shared/types/plugin'
import type { InputRouter, SelectionService, ViewRegistry } from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'

/** 变换模式 */
export type TransformMode = 'translate' | 'rotate' | 'scale'

/** 变换空间 */
export type TransformSpace = 'local' | 'world'

/** 资产服务 kind → ServiceMap key 映射 */
const ASSET_SERVICE_MAP: Partial<Record<AssetKind, string>> = {
  pointCloud: 'PointCloudService',
  'gaussian-splat': 'GaussianSplatService',
  cad: 'CadService',
}

/** 变换工具插件，对选中对象执行 translate/rotate/scale 操作 */
export class TransformPlugin implements ActivatableToolPlugin {
  /** 插件名称 */
  readonly name = 'transform'
  readonly graphicsRequirements = WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 50
  /** 所属互斥组 */
  readonly group = 'default'
  /** 依赖的服务名列表 */
  readonly dependencies = ['SelectionService', 'InputRouter'] as const

  private _active = false
  private activationToken: ActivationToken | null = null
  private context!: EngineContext
  private inputRouter!: InputRouter
  private selectionService!: SelectionService
  private viewRegistry: ViewRegistry | null = null

  /** 当前变换模式 */
  private _mode: TransformMode = 'translate'
  /** 当前变换空间 */
  private _space: TransformSpace = 'world'
  /** 是否正在拖拽 gizmo */
  private isDragging = false

  /** THREE.TransformControls 实例 */
  private gizmo: TransformControls | null = null
  /** 缓存当前帧相机 */
  private currentCamera: THREE.Camera | null = null

  /** 输入事件消费者 */
  private readonly consumer: InputConsumer = {
    name: 'transform',
    priority: 100,
    onPointerDown: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerDown(event)
    },
    onPointerMove: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerMove(event)
    },
    onPointerUp: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerUp(event)
    },
    onKeyDown: (event: NormalizedKeyEvent): boolean => {
      return this.handleKeyDown(event)
    },
  }

  /** 是否处于激活状态 */
  get isActive(): boolean {
    return this._active
  }

  /** 当前变换模式 */
  get mode(): TransformMode {
    return this._mode
  }

  /** 当前变换空间 */
  get space(): TransformSpace {
    return this._space
  }

  /**
   * 记录外部分配的激活令牌。
   *
   * @param token - 激活令牌
   */
  setActivationToken(token: ActivationToken): void {
    this.activationToken = token
  }

  /**
   * 初始化插件，缓存 EngineContext。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
  }

  /** 启动插件，解析依赖 */
  onStart(): void {
    this.inputRouter = this.context.getService('InputRouter')
    this.selectionService = this.context.getService('SelectionService')
    this.viewRegistry = getOptionalViewRegistry(this.context)
  }

  /**
   * 渲染视图前更新，若已激活则创建/更新 gizmo。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!isActiveViewFrame(this.viewRegistry, frame)) return

    if (!this._active) return
    this.currentCamera = frame.camera
    // 首次 activate 时 camera 可能为 null 导致 createGizmo 失败，此处补建
    if (!this.gizmo) {
      this.createGizmo()
    }
    this.updateGizmo()
  }

  /** 销毁插件，确保已反激活并释放 gizmo */
  onDestroy(): void {
    if (this._active && this.activationToken) this.deactivate(this.activationToken)
    this.removeGizmo()
    this.viewRegistry = null
  }

  /**
   * 激活工具，注册输入消费者并创建 gizmo。
   *
   * @param token - 激活令牌
   */
  activate(token: ActivationToken): void {
    this.validateToken(token)
    if (this._active) return
    this._active = true
    this.inputRouter.addConsumer(this.consumer)
    this.createGizmo()
  }

  /**
   * 停用工具，移除输入消费者并销毁 gizmo。
   *
   * @param token - 激活令牌
   */
  deactivate(token: ActivationToken): void {
    this.validateToken(token)
    if (!this._active) return
    this._active = false
    this.inputRouter.removeConsumer(this.consumer)
    this.removeGizmo()
    this.isDragging = false
  }

  /**
   * 设置变换模式。
   *
   * @param mode - 目标模式（translate/rotate/scale）
   */
  setMode(mode: TransformMode): void {
    if (this._mode === mode) return
    this._mode = mode
    this.updateGizmo()
    this.context.events.emit('tool.resultChanged', { name: this.name, data: { mode } })
  }

  /**
   * 设置变换空间。
   *
   * @param space - 目标空间（world/local）
   */
  setSpace(space: TransformSpace): void {
    if (this._space === space) return
    this._space = space
    this.updateGizmo()
    this.context.events.emit('tool.resultChanged', { name: this.name, data: { space } })
  }

  /**
   * 处理指针按下：仅在 TransformControls 确认命中 gizmo 并进入拖拽后才消费事件。
   * isDragging 由 TransformControls 的 'dragging-changed' 事件设置，
   * 不在此处主动设为 true，避免吞噬相机控制的拖拽事件。
   */
  private handlePointerDown(_event: NormalizedPointerEvent): boolean {
    // isDragging 已由 TransformControls 的 dragging-changed 回调同步设置
    return this.isDragging
  }

  /** 处理指针移动：仅在 gizmo 拖拽中消费 */
  private handlePointerMove(_event: NormalizedPointerEvent): boolean {
    return this.isDragging
  }

  /** 处理指针释放：仅在 gizmo 拖拽中消费 */
  private handlePointerUp(_event: NormalizedPointerEvent): boolean {
    return this.isDragging
  }

  /** 处理键盘事件：T=translate, R=rotate, S=scale 快捷键 */
  private handleKeyDown(event: NormalizedKeyEvent): boolean {
    if (!this._active) return false

    switch (event.key.toLowerCase()) {
      case 't':
        this.setMode('translate')
        return true
      case 'r':
        this.setMode('rotate')
        return true
      case 's':
        this.setMode('scale')
        return true
      default:
        return false
    }
  }

  /** 创建 TransformControls gizmo 并挂到 toolsRoot */
  private createGizmo(): void {
    if (this.gizmo) return
    const camera = this.currentCamera
    const domElement = this.context.container
    if (!camera || !domElement) return

    this.gizmo = new TransformControls(camera, domElement)
    this.gizmo.setMode(this._mode)
    this.gizmo.setSpace(this._space)

    // 拖拽状态同步
    this.gizmo.addEventListener('dragging-changed', (event) => {
      this.isDragging = event.value as boolean
    })

    // 添加 gizmo helper 到 toolsRoot
    this.context.sceneGraph.toolsRoot.add(this.gizmo.getHelper())
  }

  /** 同步 gizmo 的 camera、mode、space 和 attach 目标 */
  private updateGizmo(): void {
    if (!this.gizmo) return

    // 同步 camera（每帧可能变化）
    if (this.currentCamera) {
      this.gizmo.camera = this.currentCamera
    }

    // 同步模式和空间
    this.gizmo.setMode(this._mode)
    this.gizmo.setSpace(this._space)

    // 同步到选中对象
    const selected = this.getSelectedObject()
    if (selected) {
      if (this.gizmo.object !== selected) {
        this.gizmo.attach(selected)
      }
    } else {
      this.gizmo.detach()
    }
  }

  /** 从场景中移除并销毁 gizmo */
  private removeGizmo(): void {
    if (!this.gizmo) return
    this.gizmo.detach()
    this.context.sceneGraph.toolsRoot.remove(this.gizmo.getHelper())
    this.gizmo.dispose()
    this.gizmo = null
  }

  /** 从 SelectionService 获取当前选中的第一个 Object3D */
  private getSelectedObject(): THREE.Object3D | null {
    const selected = this.selectionService.selected
    if (selected.length === 0) return null

    const first = selected[0]
    const serviceKey = ASSET_SERVICE_MAP[first.kind]
    if (!serviceKey || !this.context.hasService(serviceKey as any)) return null

    const service = this.context.getService(serviceKey as any) as {
      getAsset(id: string): { object3D: THREE.Object3D | null } | undefined
    }
    const handle = service.getAsset(first.assetId)
    return handle?.object3D ?? null
  }

  /** 校验 ActivationToken 合法性 */
  private validateToken(token: ActivationToken): void {
    if (token !== this.activationToken)
      throw new Error('TransformPlugin: 非法的 ActivationToken，操作被拒绝')
  }
}

export default TransformPlugin
